import React, { useState, useEffect, useContext } from "react";
import { Table, Modal, Button } from "react-bootstrap";
import { jsPDF } from "jspdf";
import Loader from "../../UI/Loader";
import "jspdf-autotable";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaSearch, FaEye, FaFileExport, FaPrint, FaCheck, FaTimes, FaCopy, FaPlus } from 'react-icons/fa';
import AuthContext from "../../../store/auth-context";
import { getReportHeader, getReportStyles } from "../../../util/reportPrintHelper";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";
import { getHeaderHtml } from "../../../util/headerUtil";
import { themeColors } from "../../../util/themeColors";
import { exportTableToExcel } from "../../../util/excelExportUtil";
import { Spinner } from "react-bootstrap";
import html2pdf from "html2pdf.js";
import ConfirmModal from "../../UI/ConfirmModal";
import { useTranslation } from "react-i18next";

const CertificateReport = () => {
  const { t } = useTranslation();
  const [selectedPageSize, setSelectedPageSize] = useState("a4");
  const [selectedOrientation, setSelectedOrientation] = useState("portrait");
  const [showPreview, setShowPreview] = useState(false);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState("excel");
  const [isExporting, setIsExporting] = useState(false);

  const [searchPara, setSearchPara] = useState("");
  const [patientData, setPatientData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [certToDelete, setCertToDelete] = useState(null);

  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const { hospitalData } = authCtx;

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      if (!token || token === "EXPIRED") {
        navigate("/");
        return;
      }

      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/PatientCertificate/getAllCertificates`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch certificates");

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.message || "Error fetching certificates");
    } finally {
      setIsLoading(false);
    }
  };
  const cleanDescription = selectedDescription
    ?.replace(/^<p>/i, "")
    ?.replace(/<\/p>$/i, "");
  const onSearchPatientHandler = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=${searchPara}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patient data");

      const patients = await response.json();
      if (!patients || patients.length === 0) {
        toast.info("No patients found");
      }
      setPatientData(patients);
    } catch (err) {
      console.error("Search error:", err);
      toast.error("Error fetching patients");
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedPatientFromList = async (patient) => {
    setSelectedPatient(patient);
    setPatientData([]);

    if (!patient?.patientId) return;

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      setIsLoading(true);
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/PatientCertificate/patient/${patient.patientId}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patient certificates");

      const result = await response.json();
      setData({ content: result || [] });
    } catch (err) {
      console.error("Patient cert fetch error:", err);
      toast.error("Error fetching patient certificates");
    } finally {
      setIsLoading(false);
    }
  };

  const onDataCloseHandler = () => setPatientData([]);

  const TableHeader = () => (
    <thead className="table-secondary">
      <tr>
        <th>{t("Sr No")}</th>
        <th>{t("Date")}</th>
        <th>{t("Certificate Name")}</th>
        <th>{t("Patient Name")}</th>
        <th>{t("Action")}</th>
      </tr>
    </thead>
  );

  const handleViewDescription = (description) => {
    setSelectedDescription(description);
    setShowDescriptionModal(true);
  };

  const generatePDF = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const textWidth =
      (pdfDoc.getStringUnitWidth("Certificates Report") *
        pdfDoc.internal.getFontSize()) /
      pdfDoc.internal.scaleFactor;
    const xPos = (pageWidth - textWidth) / 2;

    const startY = drawReportHeader(pdfDoc, hospitalData, "Certificate Report", { hideSeparator: true, isExport: true, margin: 0, top: 0 });

    pdfDoc.autoTable({
      head: [["Sr No", "Issued Date", "Certificate Name", "Patient Name"]],
      body: data.content.map((item, index) => [
        index + 1,
        new Date(item.issuedDate).toLocaleDateString(),
        item.certificateName || "-",
        item.patientName || "-"
      ]),
      startY: startY + 10,
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      }
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData);

    const userName = authCtx?.userName;
    if (authCtx?.role === "ROLE_DOCTOR" && userName) {
      const pageHeight = pdfDoc.internal.pageSize.getHeight();
      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      pdfDoc.setFontSize(9);
      pdfDoc.setFont("helvetica", "normal");
      pdfDoc.setTextColor(80, 80, 80);
      pdfDoc.text(`( ${userName} )`, pageWidth - 40 - 90, pageHeight - 40, { align: "center" });
    }

    return pdfDoc;
  };

  const onExportHandler = () => {
    if (exportFormat === "excel") {
      setIsExporting(true);
      setTimeout(() => {
        exportTableToExcel("certificateTable", "Certificates_Report");
        setIsExporting(false);
      }, 500);
    } else {
      generatePDF().save("certificates_report.pdf");
    }
  };

  const onPreviewHandler = () => {
    const pdfBlob = generatePDF().output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setShowPreview(pdfUrl);
  };

  const onPrintHandler = () => {
    if (!data || !data.content || data.content.length === 0) {
      alert("No data to print");
      return;
    }

    // Create a hidden iframe for printing
    let printFrame = document.getElementById("printFrame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "printFrame";
      printFrame.style.display = "none";
      document.body.appendChild(printFrame);
    }

    const tableToPrint = document.querySelector("#certificateTable");
    const tableClone = tableToPrint.cloneNode(true);
    // Remove Action Column from Clone
    const actionHeader = tableClone.querySelector("th:last-child");
    if (actionHeader && actionHeader.innerText.toLowerCase().includes("action")) {
      actionHeader.remove();
      tableClone.querySelectorAll("tr").forEach(tr => {
        const lastCell = tr.querySelector("td:last-child");
        if (lastCell) lastCell.remove();
      });
    }

    const printContent = `
      <html>
        <head>
          <title>Certificates Report - ${new Date().toLocaleDateString()}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${getReportStyles("a4", "portrait")}
          </style>
        </head>
        <body>
          ${getReportHeader(hospitalData, "Certificates Report")}
          <div class="meta-info">
            <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
            <div><strong>Total Records:</strong> ${data?.content?.length || 0}</div>
          </div>
          ${tableClone.outerHTML}
        </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.contentWindow.focus();
    setTimeout(() => {
      printFrame.contentWindow.print();
    }, 500);
  };

  const handlePrintCertificate = (cert) => {
    const hospitalName = hospitalData?.hospitalName || "";
    const printWindow = window.open("", "_blank", "width=900,height=650");
    if (!printWindow) return;

    const themeColor = themeColors[hospitalData?.applicationThemeColour || 'TEAL']?.primary || '#006d77';

    printWindow.document.write(`
      <html>
        <head>
          <title>${cert.certificateName} - ${cert.patientName}</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 0; color: #333; }
            .cert-outer { height: 100%; display: flex; flex-direction: column; background: white; }
            .cert-border { margin: 5px 10px; padding: 20px 35px; border: 4px solid ${themeColor}; border-radius: 8px; flex-grow: 1; display: flex; flex-direction: column; }
            .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; font-family: 'Times New Roman', Times, serif; font-style: italic; font-size: 19px; border-bottom: 2.5px solid #eee; padding-bottom: 15px; line-height: 1.4; color: #111; font-weight: 600; }
            .info-left { text-align: left; }
            .info-right { text-align: right; }
            .content-area { font-family: 'Times New Roman', Times, serif; font-style: italic; font-size: 19px; line-height: 1.8; text-align: justify; flex-grow: 1; padding: 0; color: #111; font-weight: 600; }
            .footer-sig { margin-top: 35px; display: flex; justify-content: flex-end; }
            .sig-box { text-align: center; border-top: 2px solid #000; width: 220px; padding-top: 8px; }
            .sig-text { font-weight: bold; font-size: 14px; text-transform: uppercase; color: #000; font-family: 'Segoe UI', Arial, sans-serif; }
            .hosp-name { font-size: 12px; color: #555; font-family: 'Segoe UI', Arial, sans-serif; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; }
              .cert-outer { height: auto; page-break-inside: avoid; }
              .cert-border { min-height: 220mm; border: 4.5px solid ${themeColor} !important; margin: 2mm 5mm; }
              .footer-sig { margin-top: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="cert-outer">
            <div style="width: 100%;">
              ${getHeaderHtml(hospitalData)}
            </div>

            <div class="cert-border">
              <div class="header-info">
                <div class="info-left">
                  <div><strong>${t("Name")}:</strong> ${cert.patientName || ""}</div>
                  <div><strong>${t("Address")}:</strong> ${cert.patientAddress || "-"}</div>
                </div>
                <div class="info-right">
                  <div><strong>${t("Mobile")}:</strong> ${cert.patientMobile || "-"}</div>
                  <div><strong>${t("Date")}:</strong> ${new Date(cert.issuedDate).toLocaleDateString('en-GB')}</div>
                </div>
              </div>

              <div class="content-area">
                ${cert.description}
              </div>

              <div class="footer-sig">
                <div class="sig-box">
                  <div class="sig-text">${t("Authorized Signatory")}</div>
                  <div class="hosp-name">${hospitalName}</div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  const handleDeleteCertificate = (id) => {
    setCertToDelete(id);
    setShowConfirmModal(true);
  };

  const confirmDeleteHandler = async () => {
    if (!certToDelete) return;

    setIsLoading(true);
    try {
      const token = getAuthToken();
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/PatientCertificate/delete/${certToDelete}`;
      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });

      if (!response.ok) throw new Error("Failed to delete certificate");

      toast.success("Certificate deleted successfully!");
      fetchCertificates();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.message || "Error deleting certificate");
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
      setCertToDelete(null);
    }
  };

  const handleExportCertificate = (cert) => {
    const hospitalName = hospitalData?.hospitalName || "Hospital Name";
    const patientName = (cert.patientName || "certificate").replace(/\s+/g, "_");

    const element = document.createElement("div");
    const themeColor = themeColors[hospitalData?.applicationThemeColour || 'TEAL']?.primary || '#006d77';

    element.innerHTML = `
      <div style="
        width: 100%;
        min-height: 265mm;
        padding: 0;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        position: relative;
        background: white;
      ">
        
        <!-- Standardized Header -->
        <div style="width: 100%;">
          ${getHeaderHtml(hospitalData, true)}
        </div>

        <div style="margin: 5px 10px; padding: 20px 35px; border: 3px solid ${themeColor}; border-radius: 8px; flex-grow: 1; display: flex; flex-direction: column; background: #fff;">
          <!-- Structured Patient/Date Info - Harmonized with Content Style -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1.5px solid #eee; font-family: 'Times New Roman', Times, serif; font-style: italic; color: #111; font-weight: 600; font-size: 17px;">
            <div style="text-align: left; width: 60%;">
              <div style="margin-bottom: 5px;">${t("Name")} : ${cert.patientName || ""}</div>
              <div>${t("Address")} : ${cert.patientAddress || "-"}</div>
            </div>
            <div style="text-align: right; width: 40%;">
              <div style="margin-bottom: 5px;">${t("Mobile")} : ${cert.patientMobile || "-"}</div>
              <div>${t("Date")} : ${new Date(cert.issuedDate).toLocaleDateString('en-GB')}</div>
            </div>
          </div>

          <!-- Main Body Content -->
          <div style="flex-grow: 1; font-size: 18px; line-height: 1.8; color: #111; text-align: justify; padding: 0; font-family: 'Times New Roman', Times, serif; font-style: italic; font-weight: 600; letter-spacing: 0.1px;">
             ${cert.description}
          </div>

          <!-- Bottom Signature Section -->
          <div style="display: flex; justify-content: flex-end; width: 100%; margin-top: 25px;">
              <div style="text-align: center; min-width: 200px;">
                  <div style="border-top: 1.5px solid #000; margin-bottom: 5px; width: 100%;"></div>
                  <div style="font-weight: bold; font-size: 13px; color: #000; text-transform: uppercase;">${t("Authorized Signatory")}</div>
                  <div style="font-size: 11px; color: #555; margin-top: 2px;">${hospitalName}</div>
              </div>
          </div>
        </div>
      </div>
    `;

    html2pdf()
      .set({
        margin: 0,
        filename: `${patientName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      })
      .from(element)
      .save();
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="container-fluid">
        <div className="bg-light p-3 rounded shadow-sm border mb-4 d-flex justify-content-between align-items-center">
          <h5 className="textReports mb-0">{t("Certificates")}</h5>
        </div>

        {/* Patient Search */}
        <div className="bg-light p-3 rounded shadow-sm border mb-4">
          <form onSubmit={onSearchPatientHandler}>
            <div className="row g-2 align-items-center">
              <div className="col-md-4">
                <h6 className="mb-0 fw-bold text-muted">{t("Search Certificate by Patient:")}</h6>
              </div>
              <div className="col-md-8">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("Search Patient by Name or Mobile")}
                    value={searchPara}
                    onChange={(e) => setSearchPara(e.target.value)}
                    required
                    style={{ border: "1px solid #ced4da" }}
                  />
                  <button type="submit" className="btn-premium">
                    <FaSearch />
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Certificates Table */}
        {data && data.content && data.content.length > 0 && (
          <div className="printData boxes table-responsive">
            <Table id="certificateTable" striped bordered hover>
              <TableHeader />
              <tbody>
                {data.content.map((cert, index) => (
                  <tr key={cert?.id || index}>
                    <td>{index + 1}</td>
                    <td>{new Date(cert?.issuedDate).toLocaleDateString()}</td>
                    <td>{cert?.certificateName}</td>
                    <td>{cert?.patientName}</td>
                    <td>
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          size="sm"
                          className="btn-outline-premium"
                          onClick={() => handleViewDescription(cert?.description)}
                          title={t("VIEW DESCRIPTION")}
                        >
                          <FaEye />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-info"
                          className="btn-outline-premium"
                          onClick={() => handlePrintCertificate(cert)}
                          title={t("PRINT")}
                        >
                          <FaPrint />
                        </Button>
                        <Button
                          size="sm"
                          className="btn-outline-premium"
                          onClick={() => handleExportCertificate(cert)}
                          title={t("EXPORT PDF")}
                          style={{ padding: '4px 10px', minWidth: 'auto' }}
                        >
                          <FaFileExport />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="btn-outline-danger-premium"
                          onClick={() => handleDeleteCertificate(cert.id)}
                          title={t("DELETE")}
                        >
                          <i className="fa fa-trash-o"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {data && data.content && data.content.length === 0 && (
          <div className="printData boxes table-responsive">
            <Table striped bordered hover>
              <TableHeader />
              <tbody>
                <tr>
                  <td colSpan="5" className="text-center">
                    {t("No Record found")}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}

        {/* Export/Preview/Print Controls */}
        {data && data.content && data.content.length > 0 && (
          <div className="row options mt-4 bg-light p-3 rounded shadow-sm border mx-0 align-items-end">
            <div className="row g-3 justify-content-center align-items-end">
              <div className="col-auto">
                <label className="fw-bold small d-block">{t("Select Size")}</label>
                <select
                  className="form-select"
                  value={selectedPageSize}
                  onChange={(e) => setSelectedPageSize(e.target.value)}
                  style={{ width: '100px' }}
                >
                  <option value="a2">A2</option>
                  <option value="a3">A3</option>
                  <option value="a4">A4</option>
                  <option value="a5">A5</option>
                </select>
              </div>

              <div className="col-auto">
                <label className="fw-bold small d-block">{t("Orientation")}</label>
                <select
                  className="form-select"
                  value={selectedOrientation}
                  onChange={(e) => setSelectedOrientation(e.target.value)}
                  style={{ width: '130px' }}
                >
                  <option value="portrait">{t("Portrait")}</option>
                  <option value="landscape">{t("Landscape")}</option>
                </select>
              </div>

              <div className="col-auto">
                <button onClick={onPrintHandler} className="btn-premium">
                  <FaPrint className="me-2" /> {t("PRINT")}
                </button>
              </div>

              <div className="col-auto">
                <button onClick={onPreviewHandler} className="btn-premium">
                  <FaEye className="me-2" /> {t("PREVIEW")}
                </button>
              </div>

              <div className="col-auto">
                <label className="fw-bold small d-block">{t("Format")}</label>
                <select
                  className="form-select"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  style={{ width: '140px' }}
                >
                  <option value="pdf">{t("PDF Format")}</option>
                  <option value="excel">{t("Excel Format")}</option>
                </select>
              </div>

              <div className="col-auto">
                <button
                  onClick={onExportHandler}
                  className="btn-export-premium d-flex align-items-center"
                  disabled={isExporting}
                >
                  {isExporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FaFileExport className="me-2" />}
                  {isExporting ? t("EXPORTING...") : t("EXPORT")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Patient Modal */}
        {patientData?.length > 0 && (
          <Modal
            show={true}
            onHide={onDataCloseHandler}
            centered
            size="lg"
            className="patientModel"
          >
            <Modal.Header
              closeButton
              className="bg-premium text-white border-0"
            >
              <Modal.Title className="w-100 text-center text-white"><FaCheck className="me-2" /> {t("Select Patient")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t("ID")}</th>
                    <th>{t("Name")}</th>
                    <th>{t("Mobile")}</th>
                    <th>{t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {patientData.map((p) => (
                    <tr key={p.patientId}>
                      <td>{p.patientId}</td>
                      <td>
                        {[p.firstName, p.middleName, p.lastName]
                          .filter(Boolean)
                          .join(" ")}
                      </td>
                      <td>{p.mobileNumber}</td>
                      <td>
                        <Button
                          size="sm"
                          className="btn-premium"
                          onClick={() => getSelectedPatientFromList(p)}
                        >
                          <FaCheck className="me-2" /> {t("Select")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Modal.Body>
          </Modal>
        )}

        {/* PDF Preview Modal */}
        {showPreview && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header bg-premium text-white">
                  <h5 className="modal-title d-flex align-items-center">
                    <FaEye className="me-2" /> {t("CERTIFICATE REPORT PREVIEW")}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-link text-white p-0 border-0"
                    onClick={() => setShowPreview(null)}
                    aria-label="Close"
                    style={{ fontSize: "1.5rem", lineHeight: "1" }}
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="modal-body p-0 bg-dark">
                  <iframe
                    title="PDF Preview"
                    src={showPreview}
                    width="100%"
                    height="750"
                    frameBorder="0"
                    className="w-100"
                  ></iframe>
                </div>
                <div className="modal-footer bg-light p-2">
                  <Button className="btn-secondary-premium" onClick={() => setShowPreview(null)}>
                    {t("CLOSE PREVIEW")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Description Modal */}
        <Modal
          show={showDescriptionModal}
          onHide={() => setShowDescriptionModal(false)}
          centered
          size="lg"
          className="patientModel"
        >
          <Modal.Header
            closeButton
            className="bg-premium text-white border-0"
          >
            <Modal.Title className="w-100 text-center text-white">
              <FaEye className="me-2" /> {t("Certificate Description")}
            </Modal.Title>
          </Modal.Header>

          {/* <Modal.Body>
    <div className="description-container">
  <div className="description-text">
    {cleanDescription}
  </div>
</div>
  </Modal.Body> */}
          <Modal.Body>
            <div className="description-container">
              <div className="description-text text-center">
                {cleanDescription?.replace(/<\/?p>/gi, "").trim() ||
                  t("No content available...")}
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="justify-content-between">
            <Button
              className="btn-export-premium"
              onClick={() => {
                const text = cleanDescription?.replace(/<\/?p>/gi, "").trim() || "";
                navigator.clipboard.writeText(text);
                toast.success(t("Copied to clipboard"));
              }}
            >
              <FaCopy className="me-2" /> {t("Copy")}
            </Button>

            <Button
              className="btn-secondary-premium"
              onClick={() => setShowDescriptionModal(false)}
            >
              <FaTimes className="me-2" /> {t("Close")}
            </Button>
          </Modal.Footer>
        </Modal>

      </div >
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmDeleteHandler}
        title={t("Delete Certificate")}
        message={t("Are you sure you want to delete this certificate?")}
        variant="danger"
        isLoading={isLoading}
      />
    </>
  );
};

export default CertificateReport;
